import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Input,
  Spinner,
  Button,
  Collapse,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { dailyRecordsAPI, projectsAPI } from '../../helpers/api';
import SideModal from '../../components/Common/SideModal';
import DailyRecordFormModal from './DailyRecordFormModal';
import { useIsMobile } from '../../helpers/useMediaQuery';
import AuthImage, { openAuthFile } from '../../components/Common/AuthImage';

interface DailyRecord {
  id: string;
  recordDate: string;
  isCompleted: boolean;
  notes: string | null;
  createdAt: string;
  site: { name: string; project: { name: string; city: { name: string } } };
  createdBy: { id: string; firstName: string; lastName: string };
  personnelEntries: Array<{
    id: string;
    dailyWage: number;
    overtimeHours: number | null;
    personnel: { id: string; firstName: string; lastName: string; title: string | null };
  }>;
  vehicleEntries: Array<{
    id: string;
    usageType: string;
    notes: string | null;
    km: number | null;
    hours: number | null;
    driverId: string | null;
    driverType: string | null;
    vehicle: { id: string; plate: string; brand: string | null; model: string | null; isMachinery?: boolean };
  }>;
  workEntries: Array<{
    id: string;
    quantity: number;
    notes: string | null;
    siteWork: {
      id: string;
      workType: { name: string; unit: string };
    };
  }>;
  photos: Array<{
    id: string;
    fileName: string;
    filePath: string;
    description: string | null;
  }>;
}

const DailyRecordList: React.FC = () => {
  const isMobile = useIsMobile();
  const [records, setRecords] = useState<DailyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<any[]>([]);

  // Filter states
  const [siteFilter, setSiteFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DailyRecord | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Form modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editRecordId, setEditRecordId] = useState<string | null>(null);

  const openEditModal = (recordId: string) => {
    setEditRecordId(recordId);
    setFormModalOpen(true);
  };

  const openNewModal = () => {
    setEditRecordId(null);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditRecordId(null);
  };

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [siteFilter, startDate, endDate, statusFilter, searchFilter]);

  const fetchSites = async () => {
    try {
      const response = await projectsAPI.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (siteFilter) params.siteId = siteFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (statusFilter !== '') params.isCompleted = statusFilter === 'true';

      const response = await dailyRecordsAPI.getAll(params);
      let filtered = response.data;
      if (searchFilter) {
        const s = searchFilter.toLowerCase();
        filtered = filtered.filter((r: DailyRecord) =>
          r.site.name.toLowerCase().includes(s) ||
          r.site.project.name.toLowerCase().includes(s) ||
          `${r.createdBy.firstName} ${r.createdBy.lastName}`.toLowerCase().includes(s)
        );
      }
      setRecords(filtered);
    } catch (error) {
      console.error('Records fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSiteFilter('');
    setStartDate('');
    setEndDate('');
    setStatusFilter('');
    setSearchFilter('');
  };

  const hasActiveFilters = siteFilter || startDate || endDate || searchFilter;

  const openDetailModal = async (recordId: string) => {
    try {
      setLoadingDetail(true);
      setDetailModalOpen(true);
      const response = await dailyRecordsAPI.getOne(recordId);
      setSelectedRecord(response.data);
    } catch (error) {
      console.error('Record detail fetch error:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedRecord(null);
    setIsExpanded(false);
  };

  const handleEditFromDetail = () => {
    if (selectedRecord) {
      closeDetailModal();
      openEditModal(selectedRecord.id);
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    if (!window.confirm('Bu günlük kaydı silmek istediğinizden emin misiniz?')) return;
    try {
      await dailyRecordsAPI.delete(selectedRecord.id);
      toast.success('Günlük kayıt silindi');
      closeDetailModal();
      fetchRecords();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  const detailHeaderActions = (
    <>
      <button
        type="button"
        className="side-modal-expand-btn me-1"
        onClick={handleEditFromDetail}
        title="Düzenle"
      >
        <i className="ri-edit-line"></i>
      </button>
      <button
        type="button"
        className="side-modal-expand-btn me-1"
        onClick={handleDeleteRecord}
        title="Sil"
        style={{ color: '#f06548' }}
      >
        <i className="ri-delete-bin-line"></i>
      </button>
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  const getUsageTypeLabel = (type: string) => {
    switch (type) {
      case 'FULL_DAY':
        return 'Tam Gün';
      case 'HALF_DAY':
        return 'Yarım Gün';
      default:
        return type;
    }
  };

  const getDriverName = (entry: DailyRecord['vehicleEntries'][0]) => {
    if (!entry.driverId || !selectedRecord) return '-';
    if (entry.driverType === 'USER') {
      return `${selectedRecord.createdBy.firstName} ${selectedRecord.createdBy.lastName} (Formen)`;
    }
    const personnel = selectedRecord.personnelEntries.find(
      (pe) => pe.personnel.id === entry.driverId
    );
    if (personnel) {
      return `${personnel.personnel.firstName} ${personnel.personnel.lastName}`;
    }
    return '-';
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Günlük İş Kayıtları</h4>
            <div className="page-title-right">
              <button className="btn btn-primary" onClick={openNewModal}>
                <i className="ri-add-line me-1"></i>
                Yeni Kayıt
              </button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={3}>
                  <h5 className="card-title mb-0">Kayıt Listesi</h5>
                </Col>
                {!isMobile && (
                  <Col md={9}>
                    <Row className="g-2 align-items-center justify-content-end">
                      <Col md="auto">
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Ara..."
                          style={{ width: '150px' }}
                        />
                      </Col>
                      <Col md="auto">
                        <Input
                          type="select"
                          bsSize="sm"
                          value={siteFilter}
                          onChange={(e) => setSiteFilter(e.target.value)}
                          style={{ minWidth: '180px' }}
                        >
                          <option value="">Tüm Şantiyeler</option>
                          {sites.map((site: any) => (
                            <option key={site.id} value={site.id}>
                              {site.project.name} / {site.name}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md="auto">
                        <InputGroup size="sm">
                          <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ maxWidth: '140px' }}
                          />
                          <InputGroupText>-</InputGroupText>
                          <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ maxWidth: '140px' }}
                          />
                        </InputGroup>
                      </Col>
                      {hasActiveFilters && (
                        <Col md="auto">
                          <Button
                            color="light"
                            size="sm"
                            onClick={clearFilters}
                            title="Filtreleri Temizle"
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </Col>
                )}
              </Row>
            </CardHeader>
            <CardBody>
              {/* Mobile Filters */}
              {isMobile && (
                <>
                  <button
                    className="btn btn-sm btn-light w-100 mb-3 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <span>
                      <i className="ri-filter-3-line me-2"></i>
                      Filtreler
                      {hasActiveFilters && <Badge color="primary" className="ms-2">{[siteFilter, statusFilter, startDate, endDate].filter(Boolean).length}</Badge>}
                    </span>
                    <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line`}></i>
                  </button>

                  <Collapse isOpen={showFilters}>
                    <div className="bg-light p-3 rounded mb-3">
                      <div className="mb-2">
                        <label className="small text-muted mb-1">Ara</label>
                        <Input
                          type="text"
                          bsSize="sm"
                          value={searchFilter}
                          onChange={(e) => setSearchFilter(e.target.value)}
                          placeholder="Şantiye, proje, oluşturan..."
                        />
                      </div>

                      <div className="mb-2">
                        <label className="small text-muted mb-1">Şantiye</label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={siteFilter}
                          onChange={(e) => setSiteFilter(e.target.value)}
                        >
                          <option value="">Tüm Şantiyeler</option>
                          {sites.map((site: any) => (
                            <option key={site.id} value={site.id}>
                              {site.project.name} / {site.name}
                            </option>
                          ))}
                        </Input>
                      </div>

                      <Row>
                        <Col xs={6}>
                          <div className="mb-2">
                            <label className="small text-muted mb-1">Başlangıç</label>
                            <Input
                              type="date"
                              bsSize="sm"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                            />
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="mb-2">
                            <label className="small text-muted mb-1">Bitiş</label>
                            <Input
                              type="date"
                              bsSize="sm"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                            />
                          </div>
                        </Col>
                      </Row>

                      {hasActiveFilters && (
                        <button
                          className="btn btn-sm btn-outline-secondary w-100 mt-2"
                          onClick={clearFilters}
                        >
                          <i className="ri-close-line me-1"></i>
                          Filtreleri Temizle
                        </button>
                      )}
                    </div>
                  </Collapse>
                </>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : records.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Henüz kayıt bulunmuyor.</p>
                  <button className="btn btn-primary" onClick={openNewModal}>
                    İlk Kaydı Oluştur
                  </button>
                </div>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {records.map((record) => (
                    <div key={record.id} className="admin-card-item">
                      <div
                        className="admin-card-header"
                        onClick={() => openDetailModal(record.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h6 className="admin-card-title">
                          {new Date(record.recordDate).toLocaleDateString('tr-TR', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                          })}
                        </h6>
                        <Badge color="success">Kapandı</Badge>
                      </div>
                      <div
                        className="admin-card-body"
                        onClick={() => openDetailModal(record.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-building-line"></i>
                            Şantiye
                          </span>
                          <span className="admin-info-value">
                            {record.site.project.name} / {record.site.name}
                          </span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-team-line"></i>
                            Özet
                          </span>
                          <span className="admin-info-value">
                            {record.personnelEntries.length} kişi, {record.vehicleEntries.length} araç, {record.workEntries.length} iş
                          </span>
                        </div>
                      </div>
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={() => openDetailModal(record.id)}
                        >
                          <i className="ri-eye-line me-1"></i>
                          Detay
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Tarih</th>
                        <th>Şantiye</th>
                        <th>Personel</th>
                        <th>Araç</th>
                        <th>İş</th>
                        <th>Durum</th>
                        <th>Oluşturan</th>
                        <th className="text-center" style={{ width: '100px' }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((record) => (
                        <tr key={record.id}>
                          <td
                            onClick={() => openDetailModal(record.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <strong>
                              {new Date(record.recordDate).toLocaleDateString('tr-TR')}
                            </strong>
                          </td>
                          <td
                            onClick={() => openDetailModal(record.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {record.site.project.city.name} / {record.site.project.name} /{' '}
                            {record.site.name}
                          </td>
                          <td
                            onClick={() => openDetailModal(record.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {record.personnelEntries.length} kişi
                          </td>
                          <td
                            onClick={() => openDetailModal(record.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {record.vehicleEntries.length} araç
                          </td>
                          <td
                            onClick={() => openDetailModal(record.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {record.workEntries.length} iş
                          </td>
                          <td
                            onClick={() => openDetailModal(record.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <Badge color="success">Kapandı</Badge>
                          </td>
                          <td
                            onClick={() => openDetailModal(record.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {record.createdBy.firstName} {record.createdBy.lastName}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() => openDetailModal(record.id)}
                              title="Detay"
                            >
                              <i className="ri-eye-line"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Detail Side Modal */}
      <SideModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        title="Günlük Kayıt Detayı"
        size={isExpanded ? 'expanded' : 'lg'}
        headerActions={detailHeaderActions}
      >
        {loadingDetail ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : selectedRecord ? (
          <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
            {/* Header Card */}
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
              <div className="d-flex align-items-start justify-content-between">
                <div>
                  <h4 className="mb-1" style={{ fontWeight: 600 }}>
                    {new Date(selectedRecord.recordDate).toLocaleDateString('tr-TR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </h4>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    {selectedRecord.site.project.name} / {selectedRecord.site.name}
                  </p>
                </div>
                <span
                  className="px-2 py-1 rounded-pill small bg-success bg-opacity-10 text-success"
                  style={{ fontWeight: 500 }}
                >
                  Kapandı
                </span>
              </div>
              <div className="mt-3 text-muted small">
                <span>{selectedRecord.createdBy.firstName} {selectedRecord.createdBy.lastName}</span>
                <span className="mx-2">•</span>
                <span>{new Date(selectedRecord.createdAt).toLocaleString('tr-TR')}</span>
              </div>
            </div>

            {/* Notes Card */}
            {selectedRecord.notes && (
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
                <div className="text-muted small mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Not
                </div>
                <p className="mb-0" style={{ color: '#495057' }}>{selectedRecord.notes}</p>
              </div>
            )}

            {/* Personnel Card */}
            {selectedRecord.personnelEntries.length > 0 && (
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    Personel
                  </span>
                  <span className="text-muted small">{selectedRecord.personnelEntries.length} kişi</span>
                </div>
                <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                  {selectedRecord.personnelEntries.map((entry, idx) => (
                    <div
                      key={entry.id}
                      className="d-flex align-items-center justify-content-between px-3 py-2"
                      style={{ borderBottom: idx < selectedRecord.personnelEntries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div>
                        <span style={{ fontWeight: 500 }}>{entry.personnel.firstName} {entry.personnel.lastName}</span>
                        {entry.personnel.title && (
                          <span className="text-muted ms-2 small">({entry.personnel.title})</span>
                        )}
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <span className="text-muted">{entry.dailyWage} yevmiye</span>
                        {entry.overtimeHours && (
                          <Badge color="warning" className="fs-12">{entry.overtimeHours} sa mesai</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="d-flex align-items-center justify-content-between px-3 py-2 bg-white">
                    <span style={{ fontWeight: 500 }}>Toplam</span>
                    <div className="d-flex gap-2 align-items-center">
                      <span style={{ fontWeight: 600 }}>
                        {selectedRecord.personnelEntries.reduce((sum, e) => sum + Number(e.dailyWage), 0)} yevmiye
                      </span>
                      {selectedRecord.personnelEntries.reduce((sum, e) => sum + (e.overtimeHours || 0), 0) > 0 && (
                        <Badge color="warning" style={{ fontWeight: 600 }}>
                          {selectedRecord.personnelEntries.reduce((sum, e) => sum + (e.overtimeHours || 0), 0)} sa mesai
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Vehicles Card */}
            {selectedRecord.vehicleEntries.length > 0 && (
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    Araçlar
                  </span>
                  <span className="text-muted small">{selectedRecord.vehicleEntries.length} araç</span>
                </div>
                <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                  {selectedRecord.vehicleEntries.map((entry, idx) => (
                    <div
                      key={entry.id}
                      className="d-flex align-items-center justify-content-between px-3 py-2"
                      style={{ borderBottom: idx < selectedRecord.vehicleEntries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div>
                        <span style={{ fontWeight: 500 }}>{entry.vehicle.plate}</span>
                        <span className="text-muted ms-2 small">
                          {entry.vehicle.brand} {entry.vehicle.model}
                        </span>
                        {entry.driverId && (
                          <span className="text-muted ms-1 small">• {getDriverName(entry)}</span>
                        )}
                        {entry.km != null && (
                          <span className="ms-1 small" style={{ color: '#405189' }}>• {entry.km} km</span>
                        )}
                        {entry.hours != null && (
                          <span className="ms-1 small" style={{ color: '#405189' }}>• {Number(entry.hours)} saat</span>
                        )}
                      </div>
                      <span className="text-muted small">{getUsageTypeLabel(entry.usageType)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Works Card */}
            {selectedRecord.workEntries.length > 0 && (
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    Yapılan İşler
                  </span>
                  <span className="text-muted small">{selectedRecord.workEntries.length} iş</span>
                </div>
                <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                  {selectedRecord.workEntries.map((entry, idx) => (
                    <div
                      key={entry.id}
                      className="d-flex align-items-center justify-content-between px-3 py-2"
                      style={{ borderBottom: idx < selectedRecord.workEntries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div>
                        <span style={{ fontWeight: 500 }}>{entry.siteWork.workType.name}</span>
                        {entry.notes && (
                          <span className="text-muted ms-2 small">({entry.notes})</span>
                        )}
                      </div>
                      <span style={{ fontWeight: 500 }}>
                        {Number(entry.quantity).toLocaleString('tr-TR')} {entry.siteWork.workType.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photos Card */}
            {selectedRecord.photos && selectedRecord.photos.length > 0 && (
              <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    Fotoğraflar
                  </span>
                  <span className="text-muted small">{selectedRecord.photos.length} adet</span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {selectedRecord.photos.map((photo) => (
                    <div
                      key={photo.id}
                      className="rounded-3 overflow-hidden"
                      style={{
                        width: '100px',
                        height: '100px',
                        cursor: 'pointer',
                      }}
                      onClick={() => openAuthFile(photo.filePath)}
                    >
                      <AuthImage
                        src={photo.filePath}
                        alt={photo.description || photo.fileName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {selectedRecord.personnelEntries.length === 0 &&
              selectedRecord.vehicleEntries.length === 0 &&
              selectedRecord.workEntries.length === 0 && (
                <div className="text-center text-muted py-4">
                  <p className="mb-0">Bu kayıtta henüz veri bulunmuyor.</p>
                </div>
              )}
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <p className="mb-0">Kayıt yüklenemedi</p>
          </div>
        )}
      </SideModal>

      {/* Form Modal */}
      <DailyRecordFormModal
        isOpen={formModalOpen}
        toggle={closeFormModal}
        onSuccess={fetchRecords}
        recordId={editRecordId}
      />
    </>
  );
};

export default DailyRecordList;
